import React, { useState, useEffect, useContext, useCallback } from "react";
import Axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { toast } from "react-toastify";
import { API_ROUTE, Routes } from "../../Helpers/Constants";
import AuthenticationContext from "../../Store/Authentication-Context";
import decryptParameter from "../../Helpers/DeCryptParameter";
import KnownErrorPatternModal from "../MainComponents/KnownErrorPatternModal";
import { NavBar } from "../NavBar";

export default function KnownErrors() {
    const authContext = useContext(AuthenticationContext);
    const { setAuthContext } = authContext;

    const [rowData, setRowData] = useState([]);
    const [gridApi, setGridApi] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState("create");
    const [selectedPattern, setSelectedPattern] = useState(null);

    const [search, setSearch] = useState("");
    const [filterSeverity, setFilterSeverity] = useState("");
    const [filterCategory, setFilterCategory] = useState("");
    const [filterActive, setFilterActive] = useState("");

    useEffect(() => {
        if (decryptParameter(sessionStorage.getItem("ResellerID")) !== "1") {
            window.location.href = "/login";
        }
    }, []);

    const getConfig = useCallback(async () => {
        const token = await setAuthContext("", new Date());
        return {
            headers: {
                Authorisation: `Bearer ${token}`,
                CloudCoverToken: sessionStorage.getItem("CloudCoverToken"),
            },
        };
    }, [setAuthContext]);

    const loadData = useCallback(async () => {
        try {
            const config = await getConfig();
            const payload = {
                search: search || null,
                severity: filterSeverity || null,
                category: filterCategory || null,
                isActive: filterActive === "" ? null : filterActive === "true",
                sortBy: "priority",
                sortDir: "asc",
                skip: 0,
                take: 500,
            };
            const response = await Axios.post(API_ROUTE + Routes.GET_KNOWN_ERROR_PATTERNS, payload, config);
            setRowData(response.data || []);
        } catch (err) {
            if (err?.response?.status === 440) {
                window.location.href = "/TimeoutErrorHandler";
            } else {
                toast.error("Failed to load known error patterns.");
            }
        }
    }, [search, filterSeverity, filterCategory, filterActive, getConfig]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    /**
     * Task 9 phase 3B (N7) — deactivating a known error DELETES every customer suppression against it,
     * across every organisation, and reactivating does not bring them back. Customers are not notified
     * (N7), so this confirmation is the only warning anyone gets.
     *
     * The impact is read first so the administrator sees the real number rather than a vague caution.
     * That read is advisory: if it fails we still let them proceed behind a generic warning, because
     * blocking a master-admin action on a secondary lookup would be worse than a less specific message.
     */
    const GENERIC_DEACTIVATE_WARNING =
        "Deactivate this known error?\n\n"
      + "Any customer suppressions against it will be permanently removed and cannot be "
      + "restored.\n\nContinue?";

    function describeImpact(impact) {
        // "We could not read the impact" is NOT the same as "the impact is zero". A 200 with an
        // unexpected body would otherwise reassure the administrator that nothing is affected
        // immediately before an irreversible deletion, so an unreadable count falls back to the
        // generic warning instead.
        if (typeof impact?.suppressionCount !== "number"
         || typeof impact?.organizationCount !== "number") {
            return GENERIC_DEACTIVATE_WARNING;
        }

        const suppressions = impact.suppressionCount;
        const organisations = impact.organizationCount;

        if (suppressions === 0) {
            return "Deactivate this known error?\n\n"
                 + "No customer suppressions are affected.";
        }

        const s = suppressions === 1 ? "1 suppression" : `${suppressions} suppressions`;
        const o = organisations === 1 ? "1 organisation" : `${organisations} organisations`;

        return `This will permanently remove ${s} across ${o}.\n\n`
             + "Reactivating this error will NOT restore them — those customers would have to "
             + "re-create their suppressions.\n\nContinue?";
    }

    async function handleDeactivate(id) {
        try {
            const config = await getConfig();

            let message;
            try {
                const impact = await Axios.get(
                    API_ROUTE + Routes.KNOWN_ERROR_SUPPRESSION_IMPACT(id), config);
                message = describeImpact(impact.data);
            } catch {
                message = GENERIC_DEACTIVATE_WARNING;
            }

            if (!window.confirm(message)) return;

            await Axios.put(API_ROUTE + Routes.DEACTIVATE_KNOWN_ERROR_PATTERN(id), {}, config);
            toast.success("Pattern deactivated.");
            loadData();
        } catch (err) {
            toast.error(err?.response?.data || "Failed to deactivate.");
        }
    }

    async function handleReactivate(id) {
        try {
            const config = await getConfig();
            await Axios.put(API_ROUTE + Routes.REACTIVATE_KNOWN_ERROR_PATTERN(id), {}, config);
            toast.success("Pattern reactivated.");
            loadData();
        } catch (err) {
            toast.error(err?.response?.data || "Failed to reactivate.");
        }
    }

    function openCreate() {
        setSelectedPattern(null);
        setModalMode("create");
        setShowModal(true);
    }

    function openEdit(pattern) {
        setSelectedPattern(pattern);
        setModalMode("edit");
        setShowModal(true);
    }

    function openTest(pattern) {
        setSelectedPattern(pattern);
        setModalMode("test");
        setShowModal(true);
    }

    function handleModalSaved() {
        setShowModal(false);
        loadData();
    }

    const columnDefs = [
        { field: "errorId", headerName: "Error ID", sortable: true, filter: true, width: 150 },
        { field: "name", headerName: "Name", sortable: true, filter: true, flex: 1 },
        { field: "severity", headerName: "Severity", sortable: true, filter: true, width: 110 },
        { field: "category", headerName: "Category", sortable: true, filter: true, width: 120 },
        { field: "priority", headerName: "Priority", sortable: true, width: 90 },
        {
            field: "isActive",
            headerName: "Active",
            width: 90,
            cellRenderer: (params) => (
                <span className={`badge ${params.value ? "bg-success" : "bg-secondary"}`}>
                    {params.value ? "Yes" : "No"}
                </span>
            ),
        },
        { field: "version", headerName: "Version", width: 90 },
        {
            field: "updatedAt",
            headerName: "Updated At",
            sortable: true,
            width: 170,
            valueFormatter: (params) =>
                params.value ? new Date(params.value).toLocaleString() : "",
        },
        { field: "updatedBy", headerName: "Updated By", width: 130 },
        {
            headerName: "Actions",
            width: 250,
            sortable: false,
            cellRenderer: (params) => {
                const row = params.data;
                return (
                    <div style={{ display: "flex", gap: 4, alignItems: "center", height: "100%" }}>
                        <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(row)}>Edit</button>
                        {row.isActive
                            ? <button className="btn btn-sm btn-outline-warning" onClick={() => handleDeactivate(row.id)}>Deactivate</button>
                            : <button className="btn btn-sm btn-outline-success" onClick={() => handleReactivate(row.id)}>Reactivate</button>
                        }
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => openTest(row)}>Test Regex</button>
                    </div>
                );
            },
        },
    ];

    const defaultColDef = { resizable: true };

    return (
        <div>
        <NavBar page="knownErrors" authContext={authContext} />
        <div style={{ padding: 20 }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">MasterAdmin &rsaquo; Support &rsaquo; Known Errors</h5>
                <button className="btn btn-primary" onClick={openCreate}>+ Create</button>
            </div>

            {/* Filter bar */}
            <div className="d-flex gap-2 mb-3 flex-wrap">
                <input
                    type="text"
                    className="form-control"
                    style={{ maxWidth: 240 }}
                    placeholder="Search name, error ID, description…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <select className="form-select" style={{ maxWidth: 160 }} value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)}>
                    <option value="">All Severities</option>
                    <option value="Error">Error</option>
                    <option value="Warning">Warning</option>
                    <option value="Info">Info</option>
                </select>
                <input
                    type="text"
                    className="form-control"
                    style={{ maxWidth: 160 }}
                    placeholder="Category…"
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                />
                <select className="form-select" style={{ maxWidth: 160 }} value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
                    <option value="">All</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                </select>
            </div>

            {rowData.length === 0 ? (
                <div className="alert alert-light border text-center py-5">
                    <p className="mb-1 fw-semibold">No known error patterns found.</p>
                    <p className="mb-0 text-muted" style={{ fontSize: 14 }}>
                        Known error patterns can be added manually using the Create button above.
                    </p>
                </div>
            ) : (
                <div className="ag-theme-alpine" style={{ height: 520, width: "100%" }}>
                    <AgGridReact
                        columnDefs={columnDefs}
                        rowData={rowData}
                        defaultColDef={defaultColDef}
                        rowHeight={36}
                        headerHeight={36}
                        onGridReady={(params) => {
                            setGridApi(params.api);
                            params.api.sizeColumnsToFit();
                        }}
                    />
                </div>
            )}

            <KnownErrorPatternModal
                show={showModal}
                mode={modalMode}
                pattern={selectedPattern}
                onClose={() => setShowModal(false)}
                onSaved={handleModalSaved}
            />
        </div>
        </div>
    );
}
